import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/pages/pricing-b.css";
import useReveal from "./useReveal";

// "Get a quote" opens this event in Calendly's popup widget (loaded via
// the script/stylesheet in public/index.html) instead of routing to the
// intake form — falls back to a plain new-tab link if the widget script
// hasn't finished loading yet.
const CALENDLY_URL = "https://calendly.com/teja-prosapiens/ann-demo";
function openCalendly(e) {
  e.preventDefault();
  if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
    window.Calendly.initPopupWidget({ url: CALENDLY_URL });
  } else {
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
  }
}

/* ============================================================
   Pricing — "Ann Pricing" (Claude Design import)
   Same editorial register as First Principles: Newsreader +
   IBM Plex, warm cream, cyan accent — see
   Styles/pages/pricing-b.css. Site header/footer wrap this like
   every other route; the page owns only its body content.
   ============================================================ */

const TIERS = [
  {
    key: "search",
    lbl: "Search",
    name: "Search",
    forWhom: "When sourcing is your bottleneck.",
    price: "Scoped to volume",
    pricek: "by roles & candidates / month",
    stages: [true, false, false, false],
    features: [
      "Evidence-based sourcing",
      "Candidates a keyword search structurally cannot reach",
      "Works alongside your stack",
      "ATS sync included",
    ],
    featured: false,
  },
  {
    key: "full",
    lbl: "Full funnel",
    name: (
      <>
        The <em>whole</em> pipeline
      </>
    ),
    forWhom: "When you want hiring as one connected intelligence.",
    price: "Scoped to your hiring",
    pricek: "by roles & hiring volume",
    stages: [true, true, true, true],
    features: [
      "Everything in Search & Evaluate",
      "Full Search→Decide pipeline",
      "Confidence reports & evidence trail",
      "Priority support & onboarding",
    ],
    featured: true,
    ribbon: "Full pipeline",
  },
  {
    key: "evaluate",
    lbl: "Evaluate",
    name: "Evaluate",
    forWhom: "When evaluation drifts between interviewers.",
    price: "Scoped to volume",
    pricek: "by interviews / month",
    stages: [false, false, true, true],
    features: [
      "Structured, recorded rounds",
      "One rubric across candidates",
      "Confidence report per candidate",
      "Bring your own pipeline",
    ],
    featured: false,
  },
];

const STAGE_LABELS = ["Search", "Eval", "Inter", "Dec"];

const FACTORS = [
  {
    num: "01 · Scope",
    title: (
      <>
        How much you <em>hand over</em>.
      </>
    ),
    body: "Sourcing only, interviews only, or the whole pipeline. The more of the work Ann runs, the more it's doing — and the more it's worth.",
  },
  {
    num: "02 · Volume",
    title: (
      <>
        How much you <em>hire</em>.
      </>
    ),
    body: "Two senior searches a quarter and five thousand hires a year are different problems. Pricing tracks the hiring you actually run, not a flat tier. Ann's work is measured in units of evaluation, so the price tracks the work actually done rather than the size of your team.",
  },
  {
    num: "03 · Shape",
    title: (
      <>
        How your <em>team</em> works.
      </>
    ),
    body: "In-house team, multi-client agency desk, embedded RPO — each is priced to its model. Unusual setup? Bring it; we're flexible.",
  },
];

const FAQS = [
  {
    q: "Why isn't there a price list?",
    a: "Hiring needs vary widely by scope and volume — two senior searches a quarter and a high-volume annual intake are different problems. Rather than post a number that fits no one, we size a quote to your team. It takes one call, with no obligation.",
  },
  {
    q: "Do you charge per user seat?",
    a: "No. Inviting your whole team shouldn't cost more — collaboration is the point. Ann is priced to the hiring you do, not the number of logins.",
  },
  {
    q: "Can we start small and expand?",
    a: "Yes. Most teams start with the single bottleneck that hurts most — sourcing or interviews — and add the rest of the pipeline within 60 days once they've seen it work.",
  },
  {
    q: "Is there a pilot?",
    a: "We start most engagements with a real role off your desk, so you see Ann's output on your own data before committing. Bring a live role to the demo and we'll run it.",
  },
  {
    q: "How are staffing agencies priced?",
    a: (
      <>
        By desk and volume, not per placement — so the economics improve as
        you scale. See the{" "}
        <Link to="/for/staffing-firms">staffing page</Link> for how Ann fits
        contingency, RPO, and retained models.
      </>
    ),
  },
];

export default function PricingB() {
  const ref = useReveal();
  const barRef = useRef(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = (h > 0 ? (window.scrollY / h) * 100 : 0) + "%";
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="prb" ref={ref}>
      <div className="scroll-progress" ref={barRef} aria-hidden="true" />

      {/* HERO */}
      <section className="sub-hero" id="top">
        <span className="eye">Pricing</span>
        <h1>
          Priced to your <em>roles</em>, not per seat
          <span className="period" />
        </h1>
        <p className="lede-xl">
          Ann isn&rsquo;t billed by how many people log in. It&rsquo;s priced
          to the <b>hiring you actually do</b> — how much of the pipeline you
          hand over, and at what volume. Start with one bottleneck or run the
          whole thing. <em>We&rsquo;ll size it to your team.</em>
        </p>
        <div className="ctas">
          <button type="button" className="btn btn-primary btn-lg" onClick={openCalendly}>
            Get a quote
          </button>
          <a href="#factors" className="btn btn-secondary btn-lg">
            What shapes the price
          </a>
        </div>
      </section>

      {/* TIERS */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">Where to start</span>
          <h2 className="sub-h2">
            Take one part. Or the <em>whole</em> pipeline.
          </h2>
          <p className="lede">
            Three ways to engage Ann. Same intelligence underneath — you
            choose how much of the work it runs.
          </p>
        </div>

        <div className="tiers">
          {TIERS.map((t) => (
            <div key={t.key} className={`tier${t.featured ? " featured" : ""}`}>
              {t.ribbon ? <span className="ribbon">{t.ribbon}</span> : null}
              <div className="lbl">{t.lbl}</div>
              <h3>{t.name}</h3>
              <p className="for">{t.forWhom}</p>
              <div className="price">{t.price}</div>
              <div className="pricek">{t.pricek}</div>
              <div className="stages">
                {STAGE_LABELS.map((label, i) => (
                  <div key={label} className={`step ${t.stages[i] ? "on" : "off"}`}>
                    {label}
                  </div>
                ))}
              </div>
              <ul>
                {t.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <button
                type="button"
                className={`btn ${t.featured ? "btn-primary" : "btn-secondary"}`}
                onClick={openCalendly}
              >
                Get a quote
              </button>
            </div>
          ))}
        </div>

        <p className="prose-note">
          // Pricing is sized to scope and volume. A quote takes one call.
        </p>
      </section>

      {/* WHAT SHAPES THE PRICE */}
      <section className="sub-section bone" id="factors">
        <div className="sub-head">
          <span className="eye">How it&rsquo;s calculated</span>
          <h2 className="sub-h2">
            What shapes <em>your</em> price.
          </h2>
          <p className="lede">
            No tax on inviting your team. Three things actually move the
            number.
          </p>
        </div>
        <div className="factors">
          {FACTORS.map((f) => (
            <div key={f.num} className="point">
              <div className="num">{f.num}</div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">Pricing questions</span>
          <h2 className="sub-h2">
            Before you <em>ask</em>.
          </h2>
        </div>
        <div className="faq-list">
          {FAQS.map((item, i) => (
            <details className="faq-item" key={item.q} open={i === 0}>
              <summary>
                <span className="num">{String(i + 1).padStart(2, "0")}</span>
                <span className="q">{item.q}</span>
                <span className="ico" aria-hidden="true">
                  +
                </span>
              </summary>
              <p className="a">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">One call, one quote</span>
        <h2>
          Let&rsquo;s size it to <em>your</em> team.
        </h2>
        <p className="sub">
          Fifteen minutes. Bring a real role; leave with a sense of the
          output and a quote that fits how you actually hire.
        </p>
        <div className="ctas">
          <button type="button" className="btn btn-primary btn-lg" onClick={openCalendly}>
            Get a quote
          </button>
          <Link to="/first-principles" className="btn btn-secondary btn-lg">
            How Ann works
          </Link>
        </div>
      </section>
    </div>
  );
}
